import { useEffect } from "react";

// Module-private flag: whether the TrustedForm script has been injected during
// this page session. Persists across all component mounts/remounts (StrictMode
// double-invoke, route navigation), so we never inject twice.
let injected = false;

/**
 * Injects the TrustedForm (ActiveProspect) script once per page load.
 *
 * The script generates a TrustedForm certificate URL for the session and writes
 * it into a hidden input named `xxTrustedFormCertUrl`. Step 4 reads that hidden
 * field's value at submit time and includes it in the lead payload.
 *
 * Uses use_tagged_consent=true (Task 4 tags the consent button). In local dev,
 * REACT_APP_TRUSTEDFORM_SANDBOX=true appends sandbox=true so test certs aren't
 * billed. The script is intentionally kept alive across step navigation (never
 * removed) so the cert recording isn't lost.
 */
export default function TrustedFormLoader() {
  useEffect(() => {
    // Primary guard: once injected in this page session, never inject again.
    if (injected) return;

    try {
      // Secondary guard: a TrustedForm script may already be on the page from
      // outside our control (different React tree, extension, index.html).
      if (
        document.querySelector('script[src*="trustedform.com/trustedform.js"]')
      ) {
        injected = true;
        return;
      }

      const tf = document.createElement("script");
      tf.type = "text/javascript";
      tf.async = true;
      const proto =
        document.location.protocol === "https:" ? "https" : "http";
      const sandbox = process.env.REACT_APP_TRUSTEDFORM_SANDBOX === "true";
      tf.src =
        `${proto}://api.trustedform.com/trustedform.js` +
        `?field=xxTrustedFormCertUrl&use_tagged_consent=true` +
        (sandbox ? "&sandbox=true" : "") +
        `&l=${new Date().getTime()}${Math.random()}`;
      const s = document.getElementsByTagName("script")[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(tf, s);
        injected = true;
      }
    } catch (err) {
      // Non-fatal: if injection fails the funnel still works, the cert just
      // won't be captured. Don't crash the page.
    }
    // No cleanup: keep the script alive across step navigation.
  }, []);

  return null;
}
